import { LocalNotifications } from '@capacitor/local-notifications';
import { isNative } from '@/lib/platform';
import { useEditorOpenStore } from '@/features/editor/useEditorOpen';
import { useTaskStore } from '@/stores/taskStore';

let navigateFn: ((path: string) => void | Promise<void>) | null = null;

interface PendingAction {
  type: 'task' | 'routine' | 'habit';
  id: string;
  actionId: string;
}
let pendingAction: PendingAction | null = null;

export function setNotificationNavigator(navigate: (path: string) => void | Promise<void>): void {
  navigateFn = navigate;
  if (pendingAction) {
    const action = pendingAction;
    pendingAction = null;
    handleAction(action, navigate);
  }
}

function handleAction(
  action: PendingAction,
  navigate: (path: string) => void | Promise<void>,
): void {
  if (action.actionId === 'snooze') {
    handleSnooze(action);
    return;
  }

  // "start" or body tap — mark started and open
  handleStart(action, navigate);
}

function handleStart(
  action: PendingAction,
  navigate: (path: string) => void | Promise<void>,
): void {
  switch (action.type) {
    case 'task':
      useTaskStore.getState().setStatus(action.id, 'in_progress');
      void navigate('/app');
      setTimeout(() => useEditorOpenStore.getState().openEditor(action.id), 300);
      break;
    case 'routine':
      void navigate(`/app/routines/${action.id}/run`);
      break;
    case 'habit':
      void navigate('/app/habits');
      break;
  }
}

function handleSnooze(action: PendingAction): void {
  if (action.type === 'task') {
    const task = useTaskStore.getState().tasks.find((t) => t.id === action.id);
    if (task?.scheduledStart) {
      const newStart = new Date(Date.now() + 10 * 60 * 1000);
      const duration = task.scheduledEnd
        ? task.scheduledEnd.getTime() - task.scheduledStart.getTime()
        : task.durationMinutes * 60 * 1000;
      const newEnd = new Date(newStart.getTime() + duration);
      useTaskStore.getState().updateTask(action.id, {
        scheduledStart: newStart,
        scheduledEnd: newEnd,
      });
    }
  }
  // Store update triggers subscription → reschedules notifications automatically
}

export async function initNotificationListeners(): Promise<void> {
  if (!isNative()) return;

  await LocalNotifications.addListener('localNotificationActionPerformed', (event) => {
    const { actionId, notification } = event;
    const extra = notification.extra as
      | { type: 'task' | 'routine' | 'habit'; id: string }
      | undefined;

    if (!extra) return;

    const action: PendingAction = { ...extra, actionId: actionId ?? '' };

    if (navigateFn) {
      handleAction(action, navigateFn);
    } else {
      pendingAction = action;
    }
  });
}
